import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Select, Space, Spin, Typography } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { useConnection, useUserInfo } from '../../../../connection/Application';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { useQueryParam } from 'use-query-params';
import XLSX from 'xlsx';
import FileSaver from 'file-saver';

const { Text } = Typography;

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const fileExtension = '.xlsx';

const sheetHeaders = [
  { key: 'type', label: 'Type' },
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'status', label: 'Status' },
  { key: 'weight', label: 'Weight' },
  { key: 'teamWeight', label: 'Team Weight' },
  { key: 'completed', label: 'Completed' },
  { key: 'start', label: 'Start' },
  { key: 'end', label: 'End' },
  { key: 'currentValue', label: 'Current Value' },
  { key: 'progressType', label: 'Progress Type' },
  { key: 'unit', label: 'Progress unit' },
  { key: 'marksStart', label: 'Progress Start' },
  { key: 'marksEnd', label: 'Progress End' },
];

const buildWorkbook = (sheets: { name: string; rows: any[] }[]) => {
  const wb = XLSX.utils.book_new();
  for (const s of sheets) {
    const h = Object.fromEntries(sheetHeaders.map((e) => [e.key, e.label]));
    const ws = XLSX.utils.json_to_sheet([h], {
      header: sheetHeaders.map((e) => e.key),
      skipHeader: true,
    });
    XLSX.utils.sheet_add_json(
      ws,
      s.rows.map((row) =>
        Object.fromEntries(
          Object.entries(row)
            .map(([key, val]) => ({ key, val }))
            .filter((e) => sheetHeaders.find((n) => n.key === e.key))
            .map((x) => [x.key, x.val]),
        ),
      ),
      {
        header: sheetHeaders.map((e) => e.key),
        skipHeader: true,
        origin: -1,
      },
    );
    XLSX.utils.book_append_sheet(wb, ws, s.name.substring(0, 31));
  }
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: fileType });
  return data;
};

// Separate workbook builder for user-level export so we can use different headers without affecting team export
const buildUserWorkbook = (
  sheets: { name: string; rows: any[] }[],
  headers: { key: string; label: string }[],
) => {
  const wb = XLSX.utils.book_new();
  for (const s of sheets) {
    const h = Object.fromEntries(headers.map((e) => [e.key, e.label]));
    const ws = XLSX.utils.json_to_sheet([h], {
      header: headers.map((e) => e.key),
      skipHeader: true,
    });
    XLSX.utils.sheet_add_json(
      ws,
      s.rows.map((row) =>
        Object.fromEntries(
          Object.entries(row)
            .map(([key, val]) => ({ key, val }))
            .filter((e) => headers.find((n) => n.key === e.key))
            .map((x) => [x.key, x.val]),
        ),
      ),
      {
        header: headers.map((e) => e.key),
        skipHeader: true,
        origin: -1,
      },
    );
    XLSX.utils.book_append_sheet(wb, ws, s.name.substring(0, 31));
  }
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: fileType });
};

export const GoalsExportPage: FC = () => {
  const connection = useConnection();
  const [performanceCycles, setPerformanceCycles] = useState<any[]>([]);
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const cycleRef = useRef(cycle);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csv1Loading, setCsv1Loading] = useState(false);
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<number[]>([]);
  const user = useUserInfo();

  useEffect(() => {
    setLoading(true);
    connection
      .get<{ performanceCycle: any[]; closestCycle: any }>('cycles')
      .then((value) => {
        setPerformanceCycles(value.performanceCycle);
        if (cycleRef.current === undefined) {
          setCycle(value.closestCycle.id, 'replace');
        }
      })
      .finally(() => setLoading(false));
  }, [connection, setCycle]);

  useEffect(() => {
    if (cycle) {
      connection
        .get(user?.roles?.find((e) => e === 'hr' || e === 'admin') ? 'teams' : 'teams/user/scope', {
          size: 1000,
          cycleId: cycle,
        })
        .then((value) => {
          setTeams(value?.data ?? value?.teams ?? []);
        });
    }
  }, [connection, cycle, user?.roles]);

  const exportGoals = useCallback(() => {
    if (!cycle || selectedTeams.length === 0) return;
    setCsvLoading(true);
    connection
      .post('goals/export', { teamIds: selectedTeams, cycleId: cycle })
      .then((resp) => {
        const data = resp?.data ?? resp; // server wraps in { data }
        console.log(data);
        const sheets = (data ?? []).map((t: any) => {
          const rows = (t.goals ?? []).flatMap((g: any) => {
            const list: any[] = [];
            // First row: objective details with type = 'object'
            list.push({
              type: 'object',
              title: g.title,
              status: g.status,
              weight: g.weight,
              teamWeight: g.teamWeight,
              completed: g.completed,
              start: g.startDate,
              end: g.endDate,
            });
            // Following rows: each key result with type = 'keyresult'
            for (const kr of g.keyResults ?? []) {
              list.push({
                type: 'keyresult',
                title: kr.title,
                description: kr.description,
                status: kr.status,
                weight: kr.weight,
                teamWeight: kr.teamWeight,
                completed: kr.completed,
                start: '',
                end: '',
                currentValue: kr.currentValue,
                progressType: kr?.progress?.type,
                unit: kr?.progress?.unit,
                marksStart: kr?.progress?.startValue,
                marksEnd: kr?.progress?.endValue,
              });
            }
            return list;
          });
          return { name: t?.team?.name ?? 'Team-' + t?.team?.id, rows };
        });
        const blob = buildWorkbook(sheets);
        FileSaver.saveAs(
          blob,
          `${performanceCycles.find((e) => e.id == cycle)?.name ?? 'Cycle'}_Team_Goals_${new Date()
            .toISOString()
            .slice(0, 10)}${fileExtension}`,
        );
      })
      .finally(() => setCsvLoading(false));
  }, [connection, cycle, selectedTeams, performanceCycles]);

  const exportUserGoals = useCallback(() => {
    if (!cycle || selectedTeams.length === 0) return;
    setCsv1Loading(true);
    connection
      .post('goals/export/user-goals', { teamIds: selectedTeams, cycleId: cycle })
      .then((resp) => {
        const data = resp?.data ?? resp; // server wraps in { data }
        const userHeaders = [
          { key: 'userId', label: 'User ID' },
          { key: 'userName', label: 'User Name' },
          { key: 'userEmail', label: 'User Email' },
          { key: 'leader', label: 'Leader' },
          { key: 'type', label: 'Type' },
          { key: 'title', label: 'Title' },
          { key: 'description', label: 'Description' },
          { key: 'status', label: 'Status' },
          { key: 'weight', label: 'Weight' },
          { key: 'teamWeight', label: 'Team Weight' },
          { key: 'completed', label: 'Completed' },
          { key: 'start', label: 'Start' },
          { key: 'end', label: 'End' },
          { key: 'currentValue', label: 'Current Value' },
          { key: 'progressType', label: 'Progress Type' },
          { key: 'unit', label: 'Progress unit' },
          { key: 'marksStart', label: 'Progress Start' },
          { key: 'marksEnd', label: 'Progress End' },
        ];

        const sheets = (data ?? []).map((t: any) => {
          const rows: any[] = [];
          for (const u of t.users ?? []) {
            const userId = u?.user?.id;
            const userName = `${u?.user?.firstName ?? ''} ${u?.user?.lastName ?? ''}`.trim();
            const userEmail = u?.user?.email ?? '';
            const leader = u?.leader ? 'Yes' : 'No';
            for (const g of u.goals ?? []) {
              // Objective row
              rows.push({
                userId,
                userName,
                userEmail,
                leader,
                type: 'object',
                title: g.title,
                status: g.status,
                weight: g.weight,
                teamWeight: g.teamWeight,
                completed: g.completed,
                start: g.startDate,
                end: g.endDate,
              });
              // Key results
              for (const kr of g.keyResults ?? []) {
                rows.push({
                  userId,
                  userName,
                  userEmail,
                  leader,
                  type: 'keyresult',
                  title: kr.title,
                  description: kr.description,
                  status: kr.status,
                  weight: kr.weight,
                  teamWeight: kr.teamWeight,
                  completed: kr.completed,
                  start: '',
                  end: '',
                  currentValue: kr.currentValue,
                  progressType: kr?.progress?.type,
                  unit: kr?.progress?.unit,
                  marksStart: kr?.progress?.startValue,
                  marksEnd: kr?.progress?.endValue,
                });
              }
            }
          }
          return { name: t?.team?.name ?? 'Team-' + t?.team?.id, rows };
        });

        const blob = buildUserWorkbook(sheets, userHeaders);
        FileSaver.saveAs(
          blob,
          `${performanceCycles.find((e) => e.id == cycle)?.name ?? 'Cycle'}_User_OKRs_${new Date()
            .toISOString()
            .slice(0, 10)}${fileExtension}`,
        );
      })
      .finally(() => setCsv1Loading(false));
  }, [connection, cycle, selectedTeams, performanceCycles]);

  const teamOptions = useMemo(
    () => teams?.map((t) => ({ label: t?.name ?? '', value: t.id })),
    [teams],
  );

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Export Team Goals"
        subTitle="Export Objectives and Key Results to .xlsx (one sheet per team)"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {performanceCycles && (
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => {
                setCycle(value, 'replace');
                setSelectedTeams([]);
              }}
              data={performanceCycles}
            />
          )}
        </WebUxPageItem>
        <WebUxPageItem>
          <Spin spinning={loading}>
            <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <div>
                  <Text strong>Select Teams</Text>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 400, marginLeft: 12 }}
                    placeholder="Select one or more teams"
                    value={selectedTeams}
                    onChange={setSelectedTeams}
                    options={teamOptions}
                  />
                </div>
                <Space>
                  <Button onClick={() => setSelectedTeams([])}>Reset</Button>
                  <Button
                    type="default"
                    loading={csvLoading}
                    disabled={selectedTeams.length === 0 || csvLoading || csv1Loading}
                    onClick={exportGoals}>
                    Export Team XLSX
                  </Button>
                  <Button
                    type="primary"
                    loading={csv1Loading}
                    disabled={selectedTeams.length === 0 || csv1Loading || csvLoading}
                    onClick={exportUserGoals}>
                    Export User OKRs XLSX
                  </Button>
                </Space>
              </Space>
            </Space>
          </Spin>
        </WebUxPageItem>
      </WebUxPageContent>
    </WebUxPage>
  );
};

export default GoalsExportPage;
